import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MyContext } from "../MainContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faChevronLeft, faInbox } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/header';
import Footer from '../components/footer';
import LoadingScreen from '../components/loading_screen';
import { timeAgo } from '../utils/functions';
import { backendUrl } from '../utils/variables';


export default function MessageRequestsPage() {
    const { user } = useContext(MyContext);
    const navigate = useNavigate();
    
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState({});

    const API_BASE = backendUrl;


    useEffect(() => {
        if (user && user.user_id) {
            fetchMessageRequests();
            
            // Refresh requests every 30 seconds
            const interval = setInterval(() => {
                fetchMessageRequests();
            }, 30000);
            
            return () => clearInterval(interval);
        }
    }, [user]);

    const fetchMessageRequests = async () => {
        if (!user || !user.user_id) return;

        try {
            const response = await fetch(`${API_BASE}/get_message_requests.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id
                }),
            });

            const data = await response.json();
            if (data.success) {
                setRequests(data.requests || []);
            }
        } catch (error) {
            console.error('Error fetching message requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async (requestId, senderId) => {
        if (processing[requestId]) return;
        
        setProcessing(prev => ({ ...prev, [requestId]: true }));

        try {
            const response = await fetch(`${API_BASE}/accept_message_request.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    request_id: requestId,
                    user_id: user.user_id
                }),
            });

            const data = await response.json();
            if (data.success) {
                // Navigate to chat page
                navigate(`/messages/${senderId}`);
            } else {
                alert(data.messages || 'Failed to accept request');
                setProcessing(prev => ({ ...prev, [requestId]: false }));
            }
        } catch (error) {
            console.error('Error accepting request:', error);
            alert('Network error. Please try again.');
            setProcessing(prev => ({ ...prev, [requestId]: false }));
        }
    };

    const handleReject = async (requestId) => {
        if (processing[requestId]) return;
        
        setProcessing(prev => ({ ...prev, [requestId]: true }));

        try {
            const response = await fetch(`${API_BASE}/reject_message_request.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    request_id: requestId,
                    user_id: user.user_id
                }),
            });

            const data = await response.json();
            if (data.success) {
                // Remove from list
                setRequests(prev => prev.filter(req => req.id !== requestId));
            } else {
                alert(data.messages || 'Failed to reject request');
            }
        } catch (error) {
            console.error('Error rejecting request:', error);
            alert('Network error. Please try again.');
        } finally {
            setProcessing(prev => ({ ...prev, [requestId]: false }));
        }
    };

    if (!user || !user.user_id) {
        return <LoadingScreen />;
    }

    return (
        <div className="container">
            <Header />
            
            <div className="container-fluid mt-5 pt-3">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex align-items-center mb-3">
                            <FontAwesomeIcon 
                                icon={faChevronLeft} 
                                className="me-3"
                                onClick={() => navigate('/messages')}
                                style={{ cursor: 'pointer' }}
                            />
                            <h4>Message Requests</h4>
                        </div>

                        {loading ? (
                            <LoadingScreen />
                        ) : requests.length === 0 ? (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faInbox} size="3x" className="text-muted mb-3" style={{ opacity: 0.5 }} />
                                <p className="text-muted">No pending message requests</p>
                            </div>
                        ) : (
                            <div className="list-group">
                                {requests.map((request) => (
                                    <div
                                        key={request.id}
                                        className="list-group-item"
                                    >
                                        <div className="d-flex align-items-center mb-3">
                                            <img
                                                src={request.photo || `${process.env.PUBLIC_URL}/avatar.jpg`}
                                                alt={request.username}
                                                className="rounded-circle me-3"
                                                width="50"
                                                height="50"
                                            />
                                            <div className="flex-grow-1">
                                                <h6 className="mb-0">
                                                    {request.first_name} {request.last_name}
                                                </h6>
                                                <small className="text-muted">@{request.username}</small>
                                                <br />
                                                <small className="text-muted">
                                                    {timeAgo(request.created_at)}
                                                </small>
                                                {request.first_message && (
                                                    <div className="mt-2 p-2 bg-light rounded">
                                                        <p className="mb-0" style={{ fontSize: '0.9rem' }}>
                                                            {request.first_message.length > 100 
                                                                ? request.first_message.substring(0, 100) + '...' 
                                                                : request.first_message}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-success flex-fill"
                                                onClick={() => handleAccept(request.id, request.sender_id)}
                                                disabled={processing[request.id]}
                                            >
                                                <FontAwesomeIcon icon={faCheck} className="me-2" />
                                                Accept
                                            </button>
                                            <button
                                                className="btn btn-danger flex-fill"
                                                onClick={() => handleReject(request.id)}
                                                disabled={processing[request.id]}
                                            >
                                                <FontAwesomeIcon icon={faTimes} className="me-2" />
                                                Reject
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer page="messages" />
        </div>
    );
}

